import { Button, Col, Typography } from 'antd';
import IconWrap from 'components/common/icons/antIconWrap';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { setAuthHeaders } from '@tools/ui/api';
import { melioPurpleIcon } from '@tools/ui/assets';
import { BACKGROUND_IMAGE_URL, config, IMAGE_SUFFIX } from '@tools/ui/consts';
import { BackgroundImage } from '@tools/ui/enums';
import { User } from '@tools/ui/types';
import useRouter from '@tools/ui-hooks/useRouter.hook';
import { permissionStore, userStore } from '@tools/ui-stores';
import { logger } from '@tools/ui-utils/logger.util';
import { openToast } from '@tools/ui-utils/toast.util';
import { login } from '../../api/auth.api';
import { BypassLoginForm } from './bypass-login.form';
import RequestAccessModal from './request-access.modal';

const { Text } = Typography;

const MiddleContainer = styled.div`
  text-align: center;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px rgba(0, 0, 0, 0.08),
    0px 9px 28px 8px rgba(0, 0, 0, 0.05);
  height: max-content;
  width: 509px;
  position: fixed;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  padding: 30px 20px;
`;

const BackgroundContainer = styled.div<{ urlImage: string }>`
  background-image: url('${(props) => props.urlImage}');
  text-align: center;
  height: 100%;
  width: 100%;
  position: fixed;
`;

const GoogleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`;

const RetryButton = styled(Button)`
  height: 40px;
`;

const AskAdmin = styled.div`
  margin-top: 30px;
`;

const WelcomeCol = styled(Col)`
  margin-bottom: 9px;
  font-weight: 700;
  font-size: 32px;
  color: #262626;
`;

const TitleCol = styled(Col)`
  font-size: 16px;
  color: #595959;
`;

const getRandomImageUrl = (): string => {
  const images = Object.values(BackgroundImage);
  const randomImage = images[Math.floor(Math.random() * images.length)];
  const backgroundImageUrl = BACKGROUND_IMAGE_URL + randomImage + IMAGE_SUFFIX;

  return backgroundImageUrl;
};

type props = {
  user?: User;
};

const LoginPage: React.FC<React.PropsWithChildren<props>> = ({ user }) => {
  const { t, i18n } = useTranslation();
  const { setUserLoggedIn } = userStore;
  const { push } = useRouter();

  const [isGoogleOAuthScriptFailed, setIsGoogleOAuthScriptFailed] = useState(false);

  const language = i18n.language ?? 'en';

  const [isRequestVisible, setIsRequestVisible] = useState(false);

  const googleOAuthLoadFailHandler = () => {
    setIsGoogleOAuthScriptFailed(true);
    logger.error('[LoginPage] Google OAuth script failed to load', {});
  };

  const handleLogin = async (credentialResponse: CredentialResponse) => {
    try {
      permissionStore.toggleLoading(true);
      const loggedInUser = await login(credentialResponse.credential);
      setAuthHeaders(loggedInUser?.token);
      userStore.setUser(loggedInUser);
      setUserLoggedIn(true);
    } catch (err) {
      const msg = get(err, 'response.data.message', (err as Error).message);
      permissionStore.toggleLoading(false);
      openToast({ message: t('error'), description: msg });
    }
  };

  const handleFailed = () => {
    permissionStore.toggleLoading(false);
    openToast({ message: t('error'), description: t('auth.error.failed') });
  };

  const handleRefresh = () => {
    window.location.reload();
  };
  const showBypassLogin =
    config.auth.enableBypassLogin && navigator.userAgent === config.auth.testUserAgent;

  return user ? (
    <Redirect to={'/'} />
  ) : (
    <BackgroundContainer urlImage={getRandomImageUrl()}>
      <MiddleContainer>
        <div>
          <WelcomeCol> {t('auth.login.title')}</WelcomeCol>
          <TitleCol>{t('auth.login.subTitle')}</TitleCol>

          <GoogleContainer>
            <GoogleOAuthProvider
              clientId={config.google.clientId as string}
              onScriptLoadError={googleOAuthLoadFailHandler}
            >
              {isGoogleOAuthScriptFailed ? (
                <RetryButton onClick={handleRefresh}>{t('auth.error.refresh')}</RetryButton>
              ) : (
                <GoogleLogin
                  onSuccess={handleLogin}
                  onError={handleFailed}
                  locale={language}
                  width="240px"
                  useOneTap
                />
              )}
            </GoogleOAuthProvider>
          </GoogleContainer>

          {showBypassLogin ? <BypassLoginForm /> : null}

          <AskAdmin>
            <Text>{t('auth.login.adminRequest')}</Text>
            <Button
              data-testid="request-access"
              type="link"
              onClick={() => {
                setIsRequestVisible(true);
              }}
            >
              {t('auth.login.adminRequestLink')}
            </Button>
          </AskAdmin>
          <RequestAccessModal
            isRequestVisible={isRequestVisible}
            onFinish={() => {
              setIsRequestVisible(false);
            }}
          />
          <IconWrap
            component={melioPurpleIcon}
            style={{
              fontSize: 65,
              marginTop: 20,
            }}
          />
        </div>
      </MiddleContainer>
    </BackgroundContainer>
  );
};

export default LoginPage;
